'use strict';

import uuid from 'uuid';
import store from 'store';
import indexBy from 'lodash.indexby';

import groceryCategories from '../tables/grocery-categories';
import { getGroceriesForMeals, updateGroceryPackaging } from './Grocery';

import allNutrients from '../tables/nutrients';
import recommendations from '../tables/recommendations';
import allDiets from '../tables/diets';

const dietNames = allDiets.map(d => d.name);
const freshCategories = groceryCategories.filter(c => c.fresh).map(c => c.label);

export function computeEfficiencyScore(plan, assets, units_mode) {
    console.log("=== COMPUTE EFFICIENCY SCORE DEBUG ===");
    console.log("Plan items:", plan?.items?.length || 0);
    console.log("Assets foods count:", assets?.foods ? Object.keys(assets.foods).length : 0);

    let groceries = getGroceriesForMeals(plan.items, assets);
    console.log("Raw groceries count:", groceries?.length || 0);

    groceries = updateGroceryPackaging(groceries, assets.foods, units_mode);
    console.log("Updated groceries count:", groceries?.length || 0);

    const efficiencyCategories = ['Produce', 'Produce - Fruit', 'Produce - Vegetables'];

    let freshCount      = 0;

    let totalMass       = 0;
    let totalMassExcess = 0;

    let totalCost       = 0;
    let totalCostExcess = 0;

    console.log("Processing groceries for efficiency categories:", efficiencyCategories);

    groceries.forEach((grocery, i) => {
        console.log(`Grocery ${i}:`, grocery.category, grocery.exclude_from_food_waste ? '(excluded from waste)' : '');
        
        if (!efficiencyCategories.includes(grocery.category)) {
            return;
        }

        if (grocery.exclude_from_food_waste) {
            return;
        }

        freshCount++;

        totalMass       += grocery.pkg_grams * grocery.quantity;
        totalMassExcess += grocery.excess;

        totalCost       += grocery.price;
        totalCostExcess += grocery.excess_price;
    });

    console.log("Fresh count:", freshCount);
    console.log("Total mass:", totalMass);
    console.log("Total cost:", totalCost);

    if (!freshCount || !totalMass || !totalCost) {
        console.log("DEBUG: Returning score 0 due to missing data");
        console.log("=== COMPUTE EFFICIENCY SCORE DEBUG END ===");
        return { score: 0, groceries };
    }

    let freshCountScore = 1;

    [
        {min:  0, max: 10, score: 0.0},
        {min: 11, max: 15, score: 0.1},
        {min: 16, max: 20, score: 0.2},
        {min: 21, max: 25, score: 0.3},
        {min: 26, max: 30, score: 0.4},
        {min: 31, max: 35, score: 0.5},
        {min: 36, max: 45, score: 0.6},
        {min: 46, max: 55, score: 0.7},
        {min: 56, max: 65, score: 0.8},
        {min: 66, max: 70, score: 0.9},
    ].forEach(bracket => {
        if (freshCount >= bracket.min && freshCount <= bracket.max) {
            freshCountScore = bracket.score;
        }
    });

    // Efficiency score formula EF1
    const score = 1 - ((totalMassExcess / totalMass * 0.9) +
                       (totalCostExcess / totalCost * 0.0) +
                       (freshCountScore             * 0.1));

    console.log("Fresh count score:", freshCountScore);
    console.log("Mass excess ratio:", totalMassExcess / totalMass);
    console.log("Cost excess ratio:", totalCostExcess / totalCost);
    console.log("Final efficiency score:", score);
    console.log("=== COMPUTE EFFICIENCY SCORE DEBUG END ===");

    return { score, groceries };
}

export function getFoodsInfo(foods) {
    let names = [];
    let totalPantry = 0;
    let totalFresh = 0;

    Object.keys(foods).forEach(uuid => {
        const food = foods[uuid];
        names.push(food.pretty_name || food.name);

        if (food.exclude_from_groceries) {
            return;
        }

        if (freshCategories.includes(food.category)) {
            totalFresh++;
        } else {
            totalPantry++;
        }
    });

    names.sort((a, b) => a.localeCompare(b));

    return { totalFresh, totalPantry, names };
}

export function computeTags(plan, {recipes, products}) {
    let collector = [];
    let tags = [];

    plan.items.forEach(item => {
        let itemTags = [], recipe, product;

        // Do not allow recipes with duplicate tags to double count
        if (item.recipe_uuid && (recipe = recipes[item.recipe_uuid])) {
            itemTags = (recipe.tags || []).slice();
        }

        if (item.product_uuid && (product = products[item.product_uuid])) {
            itemTags = (product.tags || []).slice();
        }

        // De-duplicate item tags before counting so a double-tagged recipe or product doesn't double count.
        itemTags = itemTags.filter((v, i) => i === itemTags.indexOf(v));

        itemTags.forEach(tag => {
            collector[tag] = collector[tag] || 0;
            collector[tag]++;
        });
    });


    // Filter out diet tags, we're going to use meal analysis and avoidances to pick diet tags
    tags = Object.keys(collector).filter((tag) => {
        // Filter out any tag that isn't in every recipe
        return collector[tag] == plan.items.length &&
               !dietNames.includes(tag) &&
               !['Beginner', 'Intermediate', 'Advanced'].includes(tag);
    });

    if (collector['Advanced']) {
        tags.push('Advanced');
    } else if (collector['Intermediate']) {
        tags.push('Intermediate');
    } else if (collector['Beginner']) {
        tags.push('Beginner');
    }

    tags = tags.concat(plan.extratags).filter((v, i) => i === tags.indexOf(v));

    tags.sort((a, b) => a.localeCompare(b));

    return tags;
}

export function updatePlanItems(plan, assets) {
    let uniqueMeals = {};
    let handsOnByOffset = {};
    let nutrientsByOffset = {};
    let costPerServingTotal = 0, totalTimeTotal = 0;
    let ingredientTags = [];
    let collector = {};

    plan.items = plan.items || [];

    plan.items.forEach(meal => {
        meal.offset = meal.offset || 0;
        meal.meal_type = meal.meal_type || 'fresh';
        meal.meal = meal.meal || 'Snack';

        const mealKey = `${meal.offset}-${meal.meal}`;
        uniqueMeals[mealKey] = true;

        handsOnByOffset[meal.offset] = handsOnByOffset[meal.offset] || 0;

        if (meal.recipe_uuid && assets.recipes[meal.recipe_uuid]) {
            const recipe = assets.recipes[meal.recipe_uuid];

            (recipe.tags || []).forEach(tag => {
                collector[tag] = collector[tag] || 0;
                collector[tag]++;
            });

            meal.details_uuid = recipe.details;
            meal.recipe_image = recipe.image;
            meal.recipe_title = recipe.title;

            if (meal.meal_type === 'fresh') {
                if (recipe.avg_cost_per_serving) costPerServingTotal += recipe.avg_cost_per_serving;
                if (recipe.total_time) totalTimeTotal += recipe.total_time;
                if (recipe.hands_on_time) handsOnByOffset[meal.offset] += recipe.hands_on_time;

                ingredientTags = ingredientTags.concat(recipe.ingredient_tags || []);
            }

        } else if (meal.food_uuid && assets.foods[meal.food_uuid]) {
            const food = assets.foods[meal.food_uuid];
            ingredientTags = ingredientTags.concat(food.ingredient_tags || []);
        }
    });

    // Sort and de-duplicate ingredient tags
    ingredientTags.sort((a, b) => a.localeCompare(b));
    plan.ingredient_tags = ingredientTags.filter((v, i) => i == ingredientTags.indexOf(v));

    // Update the total time and average cost per serving fields
    plan.avg_cost_per_serving = Object.keys(uniqueMeals).length > 0
                              ? Math.round(costPerServingTotal / Object.keys(uniqueMeals).length * 100) / 100
                              : 0;

    plan.total_time = totalTimeTotal;
    plan.hands_on_time = 0;
    if (Object.keys(handsOnByOffset).length > 0) {
        // Add up all the hands on times
        const totalHandsOnTime = Object.keys(handsOnByOffset).reduce((c, k) => c + handsOnByOffset[k], 0);

        // Divide by number of days to get average daily hands on time.
        plan.hands_on_time = Math.round(totalHandsOnTime / Object.keys(handsOnByOffset).length);
    }

    const { totalFresh, totalPantry, foodNames } = getFoodsInfo(assets.foods);
    plan.total_fresh = totalFresh;
    plan.total_pantry = totalPantry;
    plan.food_names = foodNames;
    plan.tags = computeTags(plan, assets);

    return plan;
}

export function analyzePlan(plan, assets, units_mode) {
    console.log("=== ANALYZE PLAN DEBUG ===");
    console.log("Plan UUID:", plan?.uuid);
    console.log("Plan items count:", plan?.items?.length || 0);
    console.log("Assets provided:", !!assets);
    console.log("Units mode:", units_mode);

    // Is tags not set? Create it
    plan.tags = plan.tags || [];
    plan.extratags = plan.extratags || [];

    plan = updatePlanItems(plan, assets);
    console.log("Plan items after updatePlanItems:", plan?.items?.length || 0);

    // Calculate the efficiency score
    const { score, groceries } = computeEfficiencyScore(plan, assets, units_mode);
    console.log("Computed efficiency score:", score);
    console.log("Groceries count:", groceries?.length || 0);
    
    plan.efficiency_score = score;

    console.log("=== ANALYZE PLAN DEBUG END ===");
    return { plan, assets, groceries };
}

export function processVirtualPlans(vms = []) {
    vms = vms.map((vm, key) => {
        // Assign the virtual menu a uuid if it doesn't have one
        vm.uuid = vm.uuid || uuid.v4();
        return vm;
    });

    let existingIndexed = store.get('virtual-menus') || {};
    let newIndexed      = indexBy(vms, 'uuid');

    // Merge existing and incoming
    let virtualPlans = {...existingIndexed, ...newIndexed};

    // De-index them, filtering out replaced/incomplete entries
    virtualPlans = Object.keys(virtualPlans).map(k => virtualPlans[k]).filter(vm => vm && vm.items);

    // Limit to only 10 items in the stack, remove old items from the front of the list
    if (virtualPlans.length > 10) {
        virtualPlans.splice(0, virtualPlans.length - 10);
    }

    // Strip unnecessary crap from the meal plan object. We don't need to store most of it.
    const keepKeys = ['items', 'title', 'image', 'uuid', 'type', 'meal_types', 'total_fresh', 'total_pantry', 'replaced_by', 'sizes', 'explain'];
    virtualPlans.forEach(vm => {
        Object.keys(vm).forEach(key => {
            if (!keepKeys.includes(key)) {
                delete vm[key];
            }
        });

        // Loop through each item and remove stuff there too
        vm.items.forEach(item => {
            delete item.leftovers_removed;
            // delete item.type;
        });
    });

    // Reindex by uuid and put back in localstorage
    virtualPlans = indexBy(virtualPlans, 'uuid');

    store.set('virtual-menus', virtualPlans);

    return vms;
}

export function fetchAllVirtualPlans() {
    return store.get('virtual-menus') || {};
}

export function fetchVirtualPlan(uuid) {
    const virtualPlans = store.get('virtual-menus') || {};
    const plan = virtualPlans[uuid];

    // Skip plans that have been saved to the server (replaced_by is set)
    if (plan && plan.replaced_by) {
        return null;
    }

    return plan;
}

export function deleteVirtualPlan(uuid, alias) {
    const virtualPlans = store.get('virtual-menus') || {};

    if (alias) {
        if (virtualPlans[uuid]) {
            virtualPlans[uuid].replaced_by = alias;
        }
    } else {
        delete virtualPlans[uuid];
    }

    store.set('virtual-menus', virtualPlans);
}
